import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { NodeExecutionStatus } from 'types';

import Button from '@/components/buttons/Button';
import {
  PlayIcon,
  QuestionMarkCircleIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { InputField } from '@/constants/presets';
import TextareaAutosize from 'react-textarea-autosize';
import Select from '@/components/atoms/Select';
import Toggle from '@/components/atoms/Toggle';
import RadioCards from '@/components/atoms/RadioCards';
import { TipTapPromptEditor } from '../TipTapPromptEditor';
import MarkdownBlock from '@/components/Chat/MarkdownBlock';

import { SimpleTooltip } from '@/components/ui/tooltip';
import {
  createInitialTextPresetFields,
  textPresetOptions,
} from '@/lib/workflows/textPresets';

interface TextPresetNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
  readOnly?: boolean;
}

interface TextPresetConfig {
  presetSlug?: string;
  fields?: Map<string, any> | Record<string, any>;
  keywordsFromInput?: string;
}

const toFieldsMap = (value: TextPresetConfig['fields']): Map<string, any> => {
  if (value instanceof Map) return value;
  if (value && typeof value === 'object') {
    return new Map(Object.entries(value));
  }
  return new Map();
};

const serializeFields = (value: Map<string, any>) =>
  Object.fromEntries(value.entries());

const mapsDiffer = (a: Map<string, any>, b: Map<string, any>) => {
  if (a.size !== b.size) return true;
  for (const [key, value] of a.entries()) {
    if (b.get(key) !== value) return true;
  }
  return false;
};

const TEXT_PRESETS = textPresetOptions;

export function TextPresetNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
  readOnly,
}: TextPresetNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState(!Boolean(data.result));

  const executionStatus = (() => {
    const s = (data as { status?: unknown })?.status;
    return typeof s === 'string' &&
      ['idle', 'running', 'success', 'error'].includes(s)
      ? (s as NodeExecutionStatus)
      : undefined;
  })();

  const isRunning = executionStatus === 'running';

  // Auto-collapse settings when result is generated
  useEffect(() => {
    if (data.result && showSettings) {
      setShowSettings(false);
    }
  }, [data.result]);

  // Hide result if running
  const shouldShowResult = typeof data.result !== 'undefined' && !isRunning;

  const config = (data.config || {}) as TextPresetConfig;
  const [fields, setFields] = useState<Map<string, any>>(() =>
    toFieldsMap(config.fields),
  );

  const selectedPresetSlug = config.presetSlug || TEXT_PRESETS[0]?.value;
  const selectedPreset = TEXT_PRESETS.find(
    (p) => p.value === selectedPresetSlug,
  );
  const hasExtra1 = selectedPreset?.fields?.some(
    (field) => field.name === 'extra1',
  );
  const hasExtra2 = selectedPreset?.fields?.some(
    (field) => field.name === 'extra2',
  );

  // Initialize fields when preset changes
  useEffect(() => {
    if (selectedPreset && fields.size === 0) {
      const newFields = createInitialTextPresetFields(selectedPreset.fields);
      setFields(newFields);
      updateNodeData(id, {
        config: {
          ...config,
          presetSlug: selectedPreset.value,
          fields: serializeFields(newFields),
        },
      });
    }
  }, [
    selectedPresetSlug,
    selectedPreset,
    fields.size,
    updateNodeData,
    id,
    config,
  ]);

  // Sync if config.fields changes externally (e.g., persisted workflows)
  useEffect(() => {
    const mappedFields = toFieldsMap(config.fields);
    if (mapsDiffer(mappedFields, fields)) {
      setFields(mappedFields);
    }
  }, [config.fields, fields]);

  const customInputHandles = useMemo(() => {
    const keywordLabel =
      selectedPreset?.fields?.find((f) => f.name === 'keywords')?.label ||
      'Text input';
    const handleDefs: { id: string; label: string }[] = [
      { id: 'text', label: keywordLabel },
    ];

    if (hasExtra1) {
      const label =
        selectedPreset?.fields?.find((f) => f.name === 'extra1')?.label ||
        'Extra 1';
      handleDefs.push({ id: 'extra1', label });
    }
    if (hasExtra2) {
      const label =
        selectedPreset?.fields?.find((f) => f.name === 'extra2')?.label ||
        'Extra 2';
      handleDefs.push({ id: 'extra2', label });
    }

    const spacing = 100 / (handleDefs.length + 1);

    return handleDefs.map((handle, index) => (
      <React.Fragment key={handle.id}>
        {createInputHandle(
          'text',
          handle.id,
          Math.round(spacing * (index + 1)),
          handle.label,
        )}
      </React.Fragment>
    ));
  }, [hasExtra1, hasExtra2, selectedPreset]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFields = new Map(fields);
    newFields.set(fieldName, value);
    setFields(newFields);

    updateNodeData(id, {
      config: {
        ...config,
        fields: serializeFields(newFields),
      },
    });
  };

  // Handle keywords from text input
  useEffect(() => {
    const keywordsInput = (data as any)?.inputs?.text;
    if (keywordsInput && keywordsInput !== config.keywordsFromInput) {
      const newFields = new Map(fields);
      newFields.set('keywords', keywordsInput);
      setFields(newFields);

      updateNodeData(id, {
        config: {
          ...config,
          keywordsFromInput: keywordsInput,
          fields: serializeFields(newFields),
        },
      });
    }
  }, [(data as any)?.inputs?.text]);

  // Connected nodes per handle for inline reference (e.g., @Node)
  const getConnectedNodesForHandle = useCallback(
    (handleId: string) => {
      const connectedEdges = allEdges.filter(
        (edge) => edge.target === id && edge.targetHandle === handleId,
      );

      return connectedEdges
        .map((edge) => {
          const sourceNode = allNodes.find((n) => n.id === edge.source);
          return {
            id: sourceNode?.id || '',
            label: (sourceNode?.data?.label as string) || 'Node',
          };
        })
        .filter((n) => n.id);
    },
    [id, allNodes, allEdges],
  );

  // Render individual field based on type
  const renderField = (field: InputField) => {
    const value = fields.get(field.name) || '';
    const handleId =
      field.name === 'keywords'
        ? 'text'
        : field.name === 'extra1' || field.name === 'extra2'
          ? field.name
          : undefined;
    const connectedNodes =
      handleId !== undefined ? getConnectedNodesForHandle(handleId) : [];
    const isMentionField =
      field.name === 'keywords' ||
      field.name === 'extra1' ||
      field.name === 'extra2';

    // Use TipTap with mentions for keywords/extra1/extra2
    if (isMentionField) {
      return (
        <div className='space-y-1'>
          <TipTapPromptEditor
            value={value}
            onChange={(val) => handleFieldChange(field.name, val)}
            connectedNodes={connectedNodes}
            placeholder={field.placeholder || t('Type here...')}
            // readOnly={readOnly}
          />
          {field.limit && (
            <p className='text-xs text-gray-500 dark:text-gray-400 text-right'>
              {String(value).length}/{field.limit}
            </p>
          )}
          <p className='text-[11px] text-gray-500 dark:text-gray-400'>
            {t('Type @ to reference connected nodes')}
          </p>
        </div>
      );
    }

    switch (field.type) {
      case 'textarea':
        return (
          <div className='relative'>
            <TextareaAutosize
              rows={4}
              minRows={field.minRows || 4}
              maxRows={25}
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;
                if (
                  typeof field.limit === 'undefined' ||
                  newValue.length <= field.limit
                ) {
                  handleFieldChange(field.name, newValue);
                }
              }}
              placeholder={field.placeholder}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm'
              disabled={readOnly}
            />
            {field.limit && (
              <span className='absolute right-2 top-2 text-xs text-gray-400 dark:text-slate-600'>
                {value.length}/{field.limit}
              </span>
            )}
          </div>
        );

      case 'text':
        return (
          <input
            type='text'
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              if (
                typeof field.limit === 'undefined' ||
                newValue.length <= field.limit
              ) {
                handleFieldChange(field.name, newValue);
              }
            }}
            placeholder={field.placeholder}
            className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm'
            disabled={readOnly}
          />
        );

      case 'select':
        return (
          <Select
            options={field.options as string[]}
            value={value || (field.options?.[0] as string)}
            onChange={(option) => handleFieldChange(field.name, option)}
            label={field.label}
          />
        );

      case 'checkbox':
        return (
          <Toggle
            initialValue={value || field.initialValue || false}
            onChange={(checked) => handleFieldChange(field.name, checked)}
          />
        );

      case 'radio':
        // Update radioOptions to reflect current value
        const radioOptions = (field.radioOptions || []).map((opt) => ({
          ...opt,
          checked: opt.name === value,
        }));
        return (
          <RadioCards
            options={radioOptions}
            onChange={(option) => handleFieldChange(field.name, option)}
          />
        );

      case 'brand_voice':
        return (
          <input
            type='text'
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={
              field.placeholder || t('e.g., friendly, professional, witty')
            }
            className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm'
            disabled={readOnly}
          />
        );

      default:
        return null;
    }
  };

  const rawError = (data as any)?.error;
  const errorText =
    typeof rawError === 'string'
      ? rawError.trim()
      : rawError != null
        ? String(rawError).trim()
        : '';

  const resultText =
    typeof (data as any)?.result === 'string'
      ? ((data as any).result as string)
      : Array.isArray((data as any)?.result)
        ? (data as any).result.map((r: any) => r.text || '').join('\n\n')
        : typeof (data as any)?.result === 'object' &&
            (data as any)?.result !== null
          ? JSON.stringify((data as any)?.result, null, 2)
          : typeof (data as any)?.result !== 'undefined'
            ? String((data as any)?.result)
            : '';

  return (
    <>
      <BaseNode
        title={
          (typeof (data as { label?: unknown })?.label === 'string' &&
            (data as { label?: string }).label) ||
          t('Text Preset')
        }
        icon={
          <svg
            className='w-4 h-4 text-purple-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
          </svg>
        }
        hasInput={false}
        hasOutput={false}
        status={executionStatus}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        readOnly={readOnly}
        customHeaderButtons={
          data.result ? (
            <SimpleTooltip
              title={showSettings ? t('Hide settings') : t('Show settings')}
              side='top'
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}
                className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
              >
                <svg
                  className='w-5 h-5 text-gray-600 dark:text-gray-300'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                  <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </button>
            </SimpleTooltip>
          ) : undefined
        }
        customInputs={<>{customInputHandles}</>}
        customOutputs={createOutputHandle('text', 'text', 50)}
      >
        <div className='w-[450px]'>
          {/* Collapsible Settings Section */}
          <div
            className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
              showSettings ? '' : 'hidden'
            }`}
          >
            {/* Dynamic Fields Based on Selected Preset */}
            {selectedPreset?.fields && selectedPreset.fields.length > 0 && (
              <div className='space-y-3 max-h-[400px] overflow-y-auto px-2'>
                {selectedPreset.fields.map((field) => (
                  <div
                    key={field.name}
                    className={field.type === 'hidden' ? 'hidden' : ''}
                  >
                    <label
                      htmlFor={field.name}
                      className='flex items-center text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'
                    >
                      {field.label}
                      {(() => {
                        const handleId =
                          field.name === 'keywords'
                            ? 'text'
                            : field.name === 'extra1' || field.name === 'extra2'
                              ? field.name
                              : null;
                        if (!handleId) return null;
                        const connected = getConnectedNodesForHandle(handleId);
                        if (!connected.length) return null;
                        return (
                          <span className='ml-2 text-[10px] text-primary-600 dark:text-primary-400'>
                            {connected.map((n) => `@${n.label}`).join(', ')}
                          </span>
                        );
                      })()}
                      {field.description && (
                        <SimpleTooltip title={field.description}>
                          <QuestionMarkCircleIcon className='h-3 w-3 ml-1 cursor-pointer text-gray-400' />
                        </SimpleTooltip>
                      )}
                    </label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            )}

            {/* Run Button */}
            <div className='pt-1'>
              <Button
                className='w-full justify-center'
                variant='outline'
                isLoading={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode?.(nodeId || id);
                }}
              >
                {data.result ? (
                  <>
                    <RefreshIcon className='w-4 h-4 mr-2' />
                    <span>{t('Regenerate')}</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className='w-4 h-4 mr-2' />
                    <span>{t('Run')}</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Result Display */}
          {shouldShowResult && (
            <div className='px-4 pt-3 pb-3'>
              <div className='relative'>
                <div
                  className='w-full p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md text-sm text-gray-900 dark:text-white select-text cursor-text max-h-[1000px] overflow-y-auto'
                  onWheel={(e) => e.stopPropagation()}
                >
                  <MarkdownBlock message={resultText || ''} />
                </div>
                {resultText && (
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      try {
                        if (
                          typeof navigator !== 'undefined' &&
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          navigator.clipboard.writeText(resultText);
                        } else if (typeof document !== 'undefined') {
                          const textarea = document.createElement('textarea');
                          textarea.value = resultText;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textarea);
                        }
                      } catch (err) {
                        console.error('Failed to copy text', err);
                      }
                    }}
                    className='absolute top-2 right-2 px-2 py-1 text-[10px] font-medium rounded bg-white/80 dark:bg-gray-900/80 text-primary-600 dark:text-primary-300 border border-primary-300 dark:border-primary-500 shadow-sm hover:bg-primary-50 dark:hover:bg-primary-900/40'
                  >
                    {t('Copy')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {errorText && (
            <div className='mx-4 mb-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded'>
              <p className='text-xs text-red-600 dark:text-red-400'>
                {errorText}
              </p>
            </div>
          )}
        </div>
      </BaseNode>
    </>
  );
}
